import React, { FC, ReactNode } from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { User } from '../../types/sharetribe/user';
import { CurrentUser } from '../../types/sharetribe/currentUser';
import {
  ensureCurrentUser,
  ensureUser,
  userAbbreviatedName,
  userDisplayNameAsString,
} from '../../util/data';
import { IconBannedUser, NamedLink, ResponsiveImage } from '..';
import DynamicValueWrapper from '../DynamicValueWrapper/DynamicValueWrapper';

import css from './Avatar.module.css';

// Responsive image sizes hint
const AVATAR_SIZES = '40px';
const AVATAR_SIZES_MEDIUM = '60px';
const AVATAR_SIZES_LARGE = '96px';

const AVATAR_IMAGE_VARIANTS = [
  // 240x240
  'square-small',

  // 480x480
  'square-small2x',
];

interface AvatarComponentProps {
  className?: string;
  sizeClassName?: string;
  user?: User | CurrentUser | null;
  renderSizes?: string;
  disableProfileLink?: boolean;
  noProfileImageIcon?: ReactNode;
}

export const AvatarComponent: FC<AvatarComponentProps> = ({
  className,
  sizeClassName,
  user,
  renderSizes = AVATAR_SIZES,
  disableProfileLink = false,
  noProfileImageIcon,
}) => {
  const intl = useIntl();

  const classes = classNames(css.root, className, sizeClassName);
  const userIsCurrentUser = user && user.type === 'currentUser';
  const avatarUser = userIsCurrentUser
    ? ensureCurrentUser(user as CurrentUser)
    : ensureUser(user as User);

  const isBannedUser = avatarUser.attributes.banned;
  const isDeletedUser = avatarUser.attributes.deleted;

  const bannedUserDisplayName = intl.formatMessage({
    id: 'Avatar.bannedUserDisplayName',
  });

  const deletedUserDisplayName = intl.formatMessage({
    id: 'Avatar.deletedUserDisplayName',
  });

  const deletedDisplayName = isDeletedUser ? deletedUserDisplayName : '';
  const defaultUserDisplayName = isBannedUser ? bannedUserDisplayName : deletedDisplayName;

  const defaultUserAbbreviatedName = '';

  const displayName = userDisplayNameAsString(avatarUser, defaultUserDisplayName);
  const abbreviatedName = userAbbreviatedName(avatarUser, defaultUserAbbreviatedName);
  const rootProps = { className: classes, title: displayName };
  const linkProps = avatarUser.id
    ? { name: 'ProfilePage' as const, params: { id: avatarUser.id.uuid } }
    : { name: 'ProfileBasePage' as const };
  const hasProfileImage = avatarUser.profileImage && avatarUser.profileImage.id;
  const profileLinkEnabled = !disableProfileLink;

  if (isBannedUser || isDeletedUser) {
    return (
      <div {...rootProps}>
        <IconBannedUser className={css.bannedUserIcon} />
      </div>
    );
  }

  if (hasProfileImage) {
    const image = (
      <ResponsiveImage
        rootClassName={css.avatarImage}
        alt={displayName}
        image={avatarUser.profileImage}
        variants={AVATAR_IMAGE_VARIANTS}
        sizes={renderSizes}
      />
    );

    return profileLinkEnabled ? (
      <NamedLink {...rootProps} {...linkProps}>
        {image}
      </NamedLink>
    ) : (
      <div {...rootProps}>{image}</div>
    );
  }

  if (noProfileImageIcon) {
    const image = <span className={css.avatarImage}>{noProfileImageIcon}</span>;

    return profileLinkEnabled ? (
      <NamedLink {...rootProps} {...linkProps}>
        {image}
      </NamedLink>
    ) : (
      <div {...rootProps}>{image}</div>
    );
  }

  const image = (
    <span className={css.initials}>
      <DynamicValueWrapper>{abbreviatedName}</DynamicValueWrapper>
    </span>
  );

  return profileLinkEnabled ? (
    <NamedLink {...rootProps} {...linkProps}>
      {image}
    </NamedLink>
  ) : (
    <div {...rootProps}>{image}</div>
  );
};

export default AvatarComponent;

type AvatarVariantProps = Omit<AvatarComponentProps, 'sizeClassName' | 'renderSizes'>;

export const AvatarSmall: FC<AvatarVariantProps> = (props) => (
  <AvatarComponent sizeClassName={css.smallAvatar} renderSizes={AVATAR_SIZES} {...props} />
);
AvatarSmall.displayName = 'AvatarSmall';

export const AvatarMedium: FC<AvatarVariantProps> = (props) => (
  <AvatarComponent sizeClassName={css.mediumAvatar} renderSizes={AVATAR_SIZES_MEDIUM} {...props} />
);
AvatarMedium.displayName = 'AvatarMedium';

export const AvatarLarge: FC<AvatarVariantProps> = (props) => (
  <AvatarComponent sizeClassName={css.largeAvatar} renderSizes={AVATAR_SIZES_LARGE} {...props} />
);
AvatarLarge.displayName = 'AvatarLarge';
